import {
    useGetDocuments,
    useGetIndicators,
} from "@app/screens/Frameworks/framework/Csrd/pillar/data";
import { ExtensionTag } from "@app/shared/components/ExtensionTag";
import {
    makeDocumentLink,
    makeIndicatorLink,
} from "@app/shared/utils/annotations";
import { getFileFormatCategoryFromMimeType } from "@app/usecases/VersionUseCases/utils";
import { Icon } from "@design-system/Icon";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { useMemo, type FC } from "react";
import { useParams } from "react-router-dom";
import { useQuickAnswerChatTranslations } from "./i18n";

export const ReferenceBlock: FC<{
    indicatorCitations: { indicatorId: string }[];
    documentCitations: { documentId: string; citation: string }[];
}> = ({ indicatorCitations = [], documentCitations = [] }) => {
    const { org_uname } = useParams();
    const { t, keys } = useQuickAnswerChatTranslations();

    const { indicatorIds, documentIds } = useMemo(() => {
        return {
            indicatorIds: indicatorCitations.map(
                (citation) => citation.indicatorId,
            ),
            documentIds: documentCitations.map(
                (citation) => citation.documentId,
            ),
        };
    }, [indicatorCitations, documentCitations]);

    const hasReferences = indicatorIds.length > 0 || documentIds.length > 0;
    const { indicators: suggestedIndicators } = useGetIndicators(indicatorIds);
    const { documents: suggestedDocuments } = useGetDocuments(
        documentCitations.map((citation) => citation.documentId),
    );

    if (!hasReferences) {
        return <Text color="secondary">{t(keys.no_references)}</Text>;
    }

    return (
        <FlexCol gap="2">
            {suggestedIndicators.length > 0 && (
                <>
                    <Text
                        variant="caption-bold"
                        color="secondary"
                        mt="4"
                        mb="2"
                    >
                        {t(keys.indicators_title)}
                    </Text>
                    {suggestedIndicators.map((indicator) => (
                        <FlexRow key={indicator.id} alignItems="center" gap="2">
                            <Icon name="ruler" size="sm" />
                            <a
                                href={makeIndicatorLink(
                                    indicator.name,
                                    org_uname,
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:text-primary"
                            >
                                <Text variant="body-bold">
                                    {indicator.name}
                                </Text>
                            </a>
                        </FlexRow>
                    ))}
                </>
            )}
            {suggestedDocuments.length > 0 && (
                <>
                    <Text
                        variant="caption-bold"
                        color="secondary"
                        mt="4"
                        mb="2"
                    >
                        {t(keys.documents_title)}
                    </Text>
                    {suggestedDocuments.map((document) => (
                        <FlexRow key={document.id} alignItems="center" gap="2">
                            <ExtensionTag
                                name={getFileFormatCategoryFromMimeType(
                                    document?.lastApprovedVersion?.finalFile
                                        ?.mimeType || "application/pdf",
                                )}
                            />
                            <a
                                href={makeDocumentLink(
                                    {
                                        type: document.type,
                                        uniqueUrl: document.uniqueUrl,
                                    },
                                    org_uname,
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:text-primary"
                            >
                                <Text variant="body-bold">{document.name}</Text>
                            </a>
                        </FlexRow>
                    ))}
                </>
            )}
        </FlexCol>
    );
};
